import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import type { ColumnsType } from "antd/es/table";
import Table from "../../../../../../../components/CustomAntdComponents/Table";
import { ProgressBar } from "../../Summary/component";
import { useOverallRank } from "../../../../../../../../api/services/overall-rank.hooks";
import type { OverallRankItem } from "../../../../../../../../api/services/overall-rank.type";
import { useAuth } from "../../../../../../../../api/auth/useAuth";
import { LockDisplay } from "../../../../../../../components/LockDisplay/LockDisplay";
import styles from "./AkindaRankingScore.module.css";

// Helper function to get progress bar color based on score or rating
const getScoreColor = (score?: number, rating?: string): string => {
  if (rating) {
    switch (rating.toLowerCase()) {
      case "elite":
        return "#079455"; // Green
      case "excellent":
        return "#079455"; // Green
      case "good":
        return "#10B981"; // Light green
      case "average":
        return "#f79009"; // Orange
      case "above average":
        return "#10B981"; // Light green
      case "weak":
        return "#EA580C"; // Orange-red
      case "poor":
        return "#f04438"; // Red
      default:
        break;
    }
  }

  if (score !== undefined) {
    if (score >= 7) return "#079455"; // Green for good scores
    if (score >= 5) return "#f79009"; // Orange for moderate scores
    return "#f04438"; // Red for poor scores
  }

  return "#6B7280"; // Default gray
};

// Map API score names to display metric names
const scoreNameToMetricMap: Record<string, string> = {
  "profitability score": "Profitability",
  "liquidity score": "Liquidity ratio",
  "financial strength score": "Financial strength",
  "gf score": "Value",
  "growth score": "Growth",
  "momentum score": "Momentum",
  "dividend score": "Dividend",
};

// Metric order for display (API mapping keys)
const metricOrder = [
  "Profitability",
  "Liquidity ratio",
  "Financial strength",
  "Value",
  "Growth",
  "Momentum",
  "Dividend",
] as const;

const metricLabelKeys: Record<(typeof metricOrder)[number], string> = {
  Profitability: "pages.topHalal.info.peers.scoreProfitability",
  "Liquidity ratio": "pages.topHalal.info.peers.scoreLiquidityRatio",
  "Financial strength": "pages.topHalal.info.peers.scoreFinancialStrength",
  Value: "pages.topHalal.info.peers.scoreValue",
  Growth: "pages.topHalal.info.peers.scoreGrowth",
  Momentum: "pages.topHalal.info.peers.scoreMomentum",
  Dividend: "pages.topHalal.info.peers.scoreDividend",
};

interface RankingDataItem {
  key: string;
  metric: string;
  score: number;
  maxScore: number;
  color: string;
}

interface AkindaRankingScoreProps {
  tabAccessToken?: string;
}

export const AkindaRankingScore = ({
  tabAccessToken,
}: AkindaRankingScoreProps) => {
  const { t } = useTranslation();
  const { symbol } = useParams<{ symbol: string }>();
  const { user } = useAuth();

  const {
    data: overallRankData,
    isLoading,
    error,
  } = useOverallRank(symbol || "", { tabAccessToken });

  // Transform API data to table format
  const rankingData: RankingDataItem[] = React.useMemo(() => {
    if (!overallRankData?.data) return [];

    // Create a map of score name to data
    const scoreMap = new Map<string, OverallRankItem>();
    overallRankData.data.forEach((item) => {
      if (item.name !== "average score") {
        scoreMap.set(item.name.toLowerCase(), item);
      }
    });

    // Build ranking data in the correct order
    return metricOrder
      .map((metric, index) => {
        // Find the corresponding API item
        const apiItem = Array.from(scoreMap.values()).find((item) => {
          const mappedMetric = scoreNameToMetricMap[item.name.toLowerCase()];
          return mappedMetric === metric;
        });

        if (!apiItem || apiItem.total_score === undefined) {
          return null;
        }

        const score = apiItem.total_score;
        const color = getScoreColor(score, apiItem.rating);

        return {
          key: `${index + 1}`,
          metric: t(metricLabelKeys[metric]),
          score,
          maxScore: 10,
          color,
        };
      })
      .filter((item): item is RankingDataItem => item !== null);
  }, [overallRankData, t]);

  // Get average score for title
  const averageScore = overallRankData?.data.find(
    (item) => item.name === "average score",
  )?.total_score;

  const columns: ColumnsType<RankingDataItem> = [
    {
      dataIndex: "metric",
      key: "metric",
      width: 200,
      render: (text: string) => (
        <span className={styles.metricLabel}>{text}</span>
      ),
    },
    {
      dataIndex: "score",
      key: "score",
      render: (score, record) => (
        <div className={styles.progressContainer}>
          <ProgressBar
            value={record.score}
            max={record.maxScore}
            color={record.color}
            showLabel={false}
            height={12}
            backgroundColor="#F3F4F6"
          />
          {score}/{record.maxScore}
        </div>
      ),
    },
  ];

  if (!user) return <LockDisplay />;

  if (isLoading) {
    return (
      <div className={styles.tableShell}>
        <div className={styles.loadingContainer}>
          {t("pages.common.loading")}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.tableShell}>
        <div className={styles.errorContainer}>
          {t("pages.topHalal.info.rankingTables.loadingRanking", {
            message: error.message,
          })}
        </div>
      </div>
    );
  }

  const titleText =
    averageScore !== undefined
      ? t("pages.topHalal.info.rankingTables.akindaTitleWithScore", {
          score: averageScore.toFixed(2),
        })
      : t("pages.topHalal.info.rankingTables.akindaTitle");

  return (
    <div className={styles.tableShell}>
      <Table
        title={() => <div className={styles.title}>{titleText}</div>}
        columns={columns}
        dataSource={rankingData}
        pagination={false}
        showHeader={false}
        bordered={false}
      />
      <div className={styles.shellFill} aria-hidden />
    </div>
  );
};
